import { Link } from 'react-router-dom';
import type { RiskLevel } from '@wrm/shared';
import styles from './WebsiteCard.module.css';

interface Website {
  id: string;
  url: string;
  currentScore: number | null;
  lastScanAt: string | null;
}

interface WebsiteCardProps {
  website: Website;
  onScan: () => void;
  onRemove: () => void;
}

const riskLevelLabels: Record<RiskLevel, string> = {
  minimal: 'Minimal Risk',
  low: 'Low Risk',
  moderate: 'Moderate Risk',
  high: 'High Risk',
};

function getRiskLevel(score: number): RiskLevel {
  if (score < 40) return 'high';
  if (score < 60) return 'moderate';
  if (score < 80) return 'low';
  return 'minimal';
}

function formatDate(dateString: string): string {
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

export function WebsiteCard({ website, onScan, onRemove }: WebsiteCardProps) {
  const riskLevel = website.currentScore !== null 
    ? getRiskLevel(website.currentScore) 
    : null;

  const handleRemove = () => {
    if (window.confirm('Are you sure you want to remove this website?')) {
      onRemove();
    }
  };

  return (
    <div className={styles.card}>
      <div className={styles.header}>
        <div className={styles.urlWrapper}>
          <a 
            href={website.url} 
            target="_blank" 
            rel="noopener noreferrer"
            className={styles.url}
          >
            {new URL(website.url).hostname}
          </a>
        </div>
        <button 
          className={styles.removeBtn} 
          onClick={handleRemove}
          title="Remove website"
        >
          &times;
        </button>
      </div>

      <div className={styles.scoreSection}>
        {website.currentScore !== null ? (
          <>
            <div className={styles.scoreWrapper}>
              <span className={styles.scoreValue}>{website.currentScore}</span>
              <span className={styles.scoreMax}>/98</span>
            </div>
            {riskLevel && (
              <span className={`risk-badge risk-${riskLevel}`}>
                {riskLevelLabels[riskLevel]}
              </span>
            )}
          </>
        ) : (
          <span className={styles.noScore}>Not scanned yet</span>
        )}
      </div>

      <div className={styles.meta}>
        <div className={styles.metaItem}>
          <span className={styles.metaLabel}>Last scan:</span>
          <span>
            {website.lastScanAt ? formatDate(website.lastScanAt) : 'Never'}
          </span>
        </div>
      </div>

      <div className={styles.actions}>
        <button className="btn btn-secondary" onClick={onScan}>
          Scan Now
        </button>
        <Link 
          to={`/website/${website.id}`} 
          className="btn btn-secondary"
        >
          View Details
        </Link>
      </div>
    </div>
  );
}
